import * as React from 'react';
import { Component } from 'react';

import Retracked from 'js/app/retracked';
import API from 'js/lib/api';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { StyleSheet, css } from '@coursera/coursera-ui';
import { SvgArrowLeft } from '@coursera/coursera-ui/svg';

import Submit from 'bundles/authentication/components/Submit';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { MODES } from 'bundles/authentication/shared/components/authentication-modal/AuthenticationContent';
import LinkButton from 'bundles/authentication/shared/components/authentication-modal/LinkButton';
import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/authentication';

export type Props = {
  className?: string;
  navigate: (path: MODES, options?: $TSFixMe) => void;
  email: string;
  magicLinkRedirect?: string;
};

export type State = {
  magicLinkSent: boolean;
};

const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const api = API('', { type: 'rest' });
const styles = StyleSheet.create({
  container: {
    display: 'grid',
    gridGap: '24px',
    gridTemplateColumns: '1fr',
    margin: '0 auto',
    maxWidth: '396px',
    padding: '0px 30px',
  },
  description: {
    textAlign: 'center',
    wordWrap: 'break-word',
    whiteSpace: 'pre-line',
    lineHeight: '24px',
    fontFamily: 'Source Sans Pro, Arial, sans-serif',
  },
  buttonContainer: {
    display: 'grid',
    gridGap: '12px',
    gridTemplateColumns: '1fr',
    margin: '0 auto',
  },
  navigation: {
    margin: '12px auto 0',
    display: 'flex',
    textAlign: 'center',
    fontSize: '14px',
    color: '#0056D2 !important',
    textDecoration: 'underline',
    fontFamily: 'Source Sans Pro, Arial, sans-serif',
  },
  navigationIcon: {
    marginRight: '0.6rem',
  },
  section: {
    ':nth-child(1)': {
      '-ms-grid-row': '1',
    },
    ':nth-child(2)': {
      '-ms-grid-row': '2',
    },
    ':nth-child(3)': {
      '-ms-grid-row': '3',
    },
    ':nth-child(4)': {
      '-ms-grid-row': '4',
    },
  },
  title: {
    color: '#1F1F1F',
    fontFamily: 'Source Sans Pro, Arial, sans-serif',
    fontSize: '24px',
    lineHeight: '30px',
    margin: '0',
    textAlign: 'center',
    '@media (min-width: 600px)': {
      fontSize: '34px',
      lineHeight: '42px',
    },
  },
  submit: {
    marginTop: '12px',
    marginBottom: '20px',
    width: '200px',
  },
  spamText: {
    marginBottom: '0px',
    color: '#636363',
    fontSize: '14px',
    marginTop: '12px',
    fontFamily: 'Source Sans Pro, Arial, sans-serif',
    textAlign: 'center',
  },
  boldEmail: {
    fontWeight: '700',
    fontFamily: 'Source Sans Pro, Arial, sans-serif',
  },
});

class MagicLink extends Component<Props, State> {
  state = {
    magicLinkSent: false,
  };

  navigateToForgotPassword = () => {
    const { navigate } = this.props;

    navigate(MODES.REQUESTMAGICLINK);
  };

  resendLink = () => {
    const { email, magicLinkRedirect } = this.props;
    api.post('/api/magicLinks.v1/encode', { data: { email, redirectUrl: magicLinkRedirect } });
    this.setState({ magicLinkSent: true });
  };

  render() {
    const { className, email } = this.props;
    const { magicLinkSent } = this.state;

    return (
      <section {...css(className, styles.container)} aria-live="polite">
        <header {...css(styles.section)}>
          <h1 {...css(styles.title)}>{_t('Check your inbox')}</h1>
        </header>
        <p {...css(styles.description, styles.section)}>
          <FormattedMessage
            message={_t('We emailed you an login link to \n {email}. \n The link will expire shortly.')}
            email={<span {...css(styles.boldEmail)}>{email}</span>}
          />
        </p>
        <TrackedSubmit
          {...css(styles.submit, styles.section, styles.buttonContainer)}
          trackingName="resend_login_link_button"
          disabled={magicLinkSent}
          onClick={this.resendLink}
          secondary
        >
          {_t('Re-send email')}
        </TrackedSubmit>
        <div {...css(styles.section)}>
          <p {...css(styles.spamText)}>{_t("Can't find your link? Check your spam folder.")}</p>

          <LinkButton {...css(styles.navigation)} onClick={this.navigateToForgotPassword}>
            <div {...css(styles.navigationIcon)}>
              <SvgArrowLeft color="#2A73CC" size={15} />
            </div>
            {_t('Go Back')}
          </LinkButton>
        </div>
      </section>
    );
  }
}

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'magic-link-confirm-page',
  },
}))(MagicLink);
